import { useTranslations, useLocale } from "next-intl";
import { ArticleCard } from "@carfax/web-ui/components/ArticleCard";
import { Icon } from "@carfax/web-ui/elements/Icon";
import { Grid } from "@carfax/web-ui/elements/Grid";
import { BlogPost } from "@carfax/contentful/blog-content-api";
import { formatDate } from "../utils/formatDate";
import { styles } from "./Category.style";
import { useRouter } from "next/router";
import { linkUtil } from "utils/i18n/link-util";
import { Image } from "@carfax/web-ui/elements/Image";
import { useLanguage } from "hooks/useLanguage";
import { cx } from "../../../../styled-system/css";

interface CategoryProps {
  blogPosts: BlogPost[];
  categoryIconSrc: string;
  categoryIconAlt: string;
  categoryIconTitle: string;
  categoryTitle: string;
  categoryText: string;
  categoryFeatured: { url: string; width?: number; height?: number; title?: string };
}

export const Category = (props: CategoryProps) => {
  const {
    blogPosts,
    categoryIconSrc,
    categoryIconAlt,
    categoryIconTitle,
    categoryTitle,
    categoryText,
    categoryFeatured,
  } = props;
  const t = useTranslations("common");
  const locale = useLocale();
  const router = useRouter();
  const language = useLanguage();

  const mappedBlogPosts = blogPosts.map(({ createdAt, ...blogPost }) => ({
    ...blogPost,
    publishDate: formatDate(createdAt, locale),
    linkText: t("post.readMore"),
  }));

  const handleOnClick = (e) => {
    e.preventDefault();
    if (e.currentTarget.href) {
      router.push(e.currentTarget.href);
    }
  };

  return (
    <div>
      <div className={styles.divCategoryHeader()}>
        <Grid container>
          <Grid width={4}>
            <div className={styles.divTextCenteredColumn()}>
              <Icon
                icon={categoryIconSrc}
                desc={categoryIconAlt}
                title={categoryIconTitle}
                className={cx(styles.imgCategoryIcon(), "icon-color-override")}
              />
              <h2 className={styles.h1CategoryHeadline()}>{categoryTitle}</h2>
              <div dangerouslySetInnerHTML={{ __html: categoryText }} />
            </div>
          </Grid>
          <Grid width={8}>
            <div className={styles.imageCategoryHeader()}>
              <Image
                image={categoryFeatured}
                fetchPriorityHigh={false}
                loadEagerly={false}
                srcSizes={[459, 629, 736, 918, 1258, 1472]}
                sizes={`(min-width: 320px) 600px,
                        (min-width: 768px) 900px,
                        (min-width: 1024px) 1200px,
                        1500px`}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <Grid container>
        {mappedBlogPosts.map(({ linkUrl, ...article }, index) => (
          <Grid width={4} key={article.title}>
            <ArticleCard
              fetchPriority={index < 3}
              {...{ href: linkUtil.i18nLinkWithLanguage(linkUrl, language), onClick: handleOnClick, ...article }}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};
